import { Box, SxProps, Theme } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { CellDrillDownInfoTypes, DrillDownInfo, MeasureDrillDownInfo } from "../../../../api/biApi.types";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectDrillDownCacheValid } from "../../../../store/selectors/currentReportSelectors";
import { DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import { createDrillDownPageAction } from "../../../../store/utils/storeUtils";
import ExpandableDialog from "../../../ExpandableDialog";
import DrillDownConfigurationLoader from "./DrillDownConfigurationLoader";
import IrrPage from "./irr/IrrPage";
import { isIrrDrillDownInfo, isMeasureDrillDownInfo, isMetricDrillDownInfo } from "./isMeasureDrillDownInfo";
import LedgerPage from "./ledger/LedgerPage";
import LedgerTitle from "./LedgerTitle";
import Loader from "./Loader";
import MainPage from "./MainPage";
import PageTitle from "./PageTitle";
import PmPage from "./pm/PmPage";
import PortfolioMonitoringTitle from "./PortfolioMonitoringTitle";
interface Props {
  item: DrillDownPage;
  index: number;
}
export default function DrillDownPageContainer({ item, index }: Props) {
  const dispatch = useAppDispatch();
  const isCacheValid = useSelector(selectDrillDownCacheValid);

  const handleDialogClose = useCallback(
    () => dispatch(createDrillDownPageAction("remove", item.id)),
    [dispatch, item.id]
  );

  return (
    <ExpandableDialog
      title={item?.info ? <PageTitleWrapper configuration={item} info={item.info.combined} /> : <EmptyHeader />}
      separateTitle={item.onlyLedger}
      dialogSx={{ mx: 5 + index * 4 }}
      backdropSx={getBackgropSx(index)}
      onClose={handleDialogClose}
    >
      <DrillDownConfigurationLoader>
        {item.info === undefined || !isCacheValid ? (
          <Loader key={item.id} configuration={item} />
        ) : (
          <DrillDownWrapper key={item.id} configuration={item} info={item.info} />
        )}
      </DrillDownConfigurationLoader>
    </ExpandableDialog>
  );
}

function PageTitleWrapper({ configuration, info }: { configuration: DrillDownPage; info: CellDrillDownInfoTypes }) {
  if (configuration.onlyLedger) {
    return <LedgerTitle configuration={configuration} info={info as MeasureDrillDownInfo} />;
  }
  if (isMeasureDrillDownInfo(info)) {
    return <PageTitle configuration={configuration} info={info} />;
  }
  if (isMetricDrillDownInfo(info)) {
    return <PortfolioMonitoringTitle configuration={configuration} />;
  }
  return <>Unknown drill down info type</>;
}

interface DrillDownWrapperProps {
  configuration: DrillDownPage;
  info: DrillDownInfo;
}
function DrillDownWrapper({ configuration, info }: DrillDownWrapperProps) {
  const irrInfo = isIrrDrillDownInfo(info.combined);
  if (irrInfo) {
    return <IrrPage configuration={configuration} drillDownInfo={info} />;
  }
  if (isMetricDrillDownInfo(info.combined)) {
    return <PmPage configuration={configuration} info={info.combined} />;
  }
  if (configuration.onlyLedger || configuration.measure?.meta.name === "Shares") {
    return <LedgerPage configuration={configuration} info={info.combined} />;
  }
  return <MainPage configuration={configuration} info={info} />;
}

function EmptyHeader() {
  return <Box sx={{ minHeight: 60 }}></Box>;
}

function getBackgropSx(depth: number): SxProps<Theme> {
  const capacity = ((2 + depth) / 10).toFixed(1);

  return {
    backgroundColor: `rgba(0,0,0,${capacity})`,
  };
}
